(ns app.domain.group.number-format-utils
  "Utility functions for forming or parsing a workspace's municipal case id (permit number)
  based on the group's case id template"
  (:require [app.domain.group.config.model :as config-model]
            [clojure.string :as str]
            [potpuri.core :as p]))

;;
;; Template parsing utils
;;

(def default-template-str "PSYYYYSN")

(def config-type-specific-default-templates
  {:counter-config.type/deficiency       "PSN"
   :counter-config.type/inspection-photo "PSN"})

(defn template-str->template-vec [string]
  (some->> string (re-seq #"P|S|YYYY|YY|N|C") vec))

(defn group->template-vec [group]
  (-> (:group/municipal-case-id-template group)
      (or default-template-str)
      template-str->template-vec))

(defn validate-template
  "Validates a workspace case id template string (e.g. \"PSYYYYSN\") against the rules of the case id configuration.

  `separator` is the configured separator string, `nil` when it is not set.
  Returns a vector with the violated rules in order of significance, empty when the template is valid:

    :invalid-characters              only P, S, Y and N are available for workspace case ids
    :year-or-prefix-required         the template must contain a year or a prefix
    :counter-required                the template must contain the sequential number
    :rollover-requires-year          a counter that rolls over yearly needs a year in the template
    :year-only-renewal-requires-year renewing by changing only the year needs a year in the template
    :year-counter-adjacent           the year and the sequential number need a separator between them
    :separator-required              a separator must be set and, when the template has a year, not blank"
  [{:keys [template separator rollover? year-only-renewal?]}]
  {:pre [(string? template)]}
  (let [included? #(str/includes? template %)
        year?     (included? "Y")]
    (cond-> []
      (not (re-matches #"[PYSN]+" template))
      (conj :invalid-characters)

      (not (or (included? "P") year?))
      (conj :year-or-prefix-required)

      (not (included? "N"))
      (conj :counter-required)

      (and rollover? (not year?))
      (conj :rollover-requires-year)

      (and year-only-renewal? (not year?))
      (conj :year-only-renewal-requires-year)

      (or (included? "YN")
          (included? "NY")
          (and (str/blank? separator)
               (or (included? "NSY")
                   (included? "YSN"))))
      (conj :year-counter-adjacent)

      (or (nil? separator)
          (and year? (str/blank? separator)))
      (conj :separator-required))))

;;
;; Template regex matching utils
;;

(defn- escape-regex [char]
  (if (#{\| \\ \[ \] \( \) \# \/ \? \. \{ \} \* \+ \$ \^} char) (str \\ char) char))

(defn- str->pattern [text]
  (->> text (map escape-regex) (apply str)))

(defn- custom-component-pattern
  "Matches a custom component, which can not contain the separator"
  [separator]
  (if (seq separator)
    (str "((?:(?!" (str->pattern separator) ").)+)")
    "(.+?)"))

(defn template-pattern
  "Returns a regex that matches the given template with named subgroups for the different parts"
  [template-vec prefix separator]
  (->> template-vec
       (map #(case %
               "P" (str "(" (or (str->pattern prefix) "") ")")
               "S" (str "(" (or (str->pattern separator) "") ")")
               "YY" "(?<yearTwoDigits>\\d{2})"
               "YYYY" "(?<yearFourDigits>\\d{4})"
               "N" "(?<counter>[0-9]+)"
               "C" (custom-component-pattern separator)))
       str/join
       re-pattern))


(defn updated-config-template-pattern
  "Returns a regex that will match case ids for a group config template pattern that may not yet be in the database.
  Note that the value can be inherited from the group itself"
  [group-config updates]
  (let [updated-val #(or (get updates %)
                         (config-model/resolve-config-value % group-config)
                         (get (:group.config/group group-config)
                              (keyword "group" (name %))))]
    (template-pattern
      (template-str->template-vec (or (:group.config/municipal-case-id-template group-config)
                                      (-> group-config
                                          :group.config/group
                                          :group/municipal-case-id-template)
                                      default-template-str))
      (updated-val :group.config/municipal-case-id-prefix)
      (updated-val :group.config/municipal-case-id-separator))))


(defn updated-group-template-patterns
  "Returns a seq of regexes that will match case ids for a group template pattern which may not yet be in the database
  (and group-config patterns that share the same counter)"
  [group updates]
  (let [updated-val #(or (get updates %)
                         (get group %))]
    (concat
      ;; The pattern of the group itself
      [(template-pattern
         (template-str->template-vec (or (updated-val :group/municipal-case-id-template)
                                         default-template-str))
         (updated-val :group/municipal-case-id-prefix)
         (updated-val :group/municipal-case-id-separator))]
      ;; Any group-configs which share the same counter and so could also cause collisions
      (->> group
           :group.config/_group
           (filter #(and (:group.config/municipal-case-id-prefix %)
                         (:group.config/municipal-case-id-common-counter? %)))
           (map #(updated-config-template-pattern % nil))))))


(defn updated-group-rollover
  "Returns the updated or current value for the group's municipal case id rollover
  (whether the counter is reset every year)
  Note that there is no separate attribute for this for the group-config"
  [group updates]
  (if (some? (:group/municipal-case-id-rollover updates))
    (:group/municipal-case-id-rollover updates)
    (:group/municipal-case-id-rollover group)))

(defn resolve-workspace-template
  "Resolves the municipal case-ID template for a workspace, returning template tokens."
  [workspace]
  (-> (config-model/resolve-value :group.config/municipal-case-id-template workspace)
      (or (-> workspace :domain/authority-group :group/municipal-case-id-template)
          default-template-str)
      template-str->template-vec))

(defn- workspace-number-format [workspace]
  {:template  (resolve-workspace-template workspace)
   :prefix    (or (config-model/resolve-value :group.config/municipal-case-id-prefix workspace)
                  (-> workspace :domain/authority-group :group/municipal-case-id-prefix))
   :separator (or (config-model/resolve-value :group.config/municipal-case-id-separator workspace)
                  (-> workspace :domain/authority-group :group/municipal-case-id-separator))
   :digits    (or (config-model/resolve-value :group.config/municipal-case-id-counter-digits workspace)
                  (-> workspace :domain/authority-group :group/municipal-case-id-counter-digits)
                  1)})

(defn- destruct-pattern
  "Like `template-pattern` with a capture group per template token, but also accepts a number whose prefix, or the
  separator following the prefix, is missing."
  [template-vec prefix separator]
  (->> template-vec
       (map-indexed (fn [idx token]
                      (case token
                        "P" (str "(" (str->pattern prefix) ")?")
                        "S" (str "(" (str->pattern separator) ")"
                                 (when (or (zero? idx) (= "P" (nth template-vec (dec idx)))) "?"))
                        "YY" "(\\d{2})"
                        "YYYY" "(\\d{4})"
                        "N" "([0-9]+)"
                        "C" (custom-component-pattern separator))))
       str/join
       re-pattern))

(defn destruct-number
  "Destructures `number` into a token-value map using the resolved workspace template.
  A missing prefix, or a missing separator next to the prefix, is accepted. The result always includes the configured
  prefix and separator. Returns nil when the number cannot match."
  [workspace number]
  (let [{:keys [template prefix separator]} (workspace-number-format workspace)]
    (when (and (seq number)
               (or separator (not (some #{"S"} template))))
      (some->> (re-matches (destruct-pattern template prefix separator) number)
               rest
               (map vector template)
               (remove (comp #{"P" "S"} first))
               (into (p/assoc-if {} "P" prefix "S" separator))))))

(defn workspace-serial
  "Returns a workspace's stored municipal case ID serial, or parses `number` using that workspace's format.
   The workspace is required; `number` may be nil when the stored serial is known."
  [workspace number]
  {:pre [(some? workspace)]}
  (or (:workspace/municipal-case-id-serial workspace)
      (some-> (destruct-number workspace number)
              (get "N")
              parse-long)))

(defn generate-number
  "Generates a number from the resolved workspace template and token-value map.
  Prefix and separator values are taken from the resolved workspace configuration,
  and the serial is padded to the configured counter digit count. Custom token values
  (such as `C`) are preserved from `values`. Missing token values produce nil."
  [workspace values]
  (let [{:keys [template prefix separator digits]} (workspace-number-format workspace)
        values                                     (assoc values "P" prefix "S" separator)
        value-for                                  (fn [token]
                                                     (let [value (get values token)]
                                                       (case token
                                                         "N" (when (some? value)
                                                               (let [value (str value)]
                                                                 (str (apply str (repeat (max 0 (- (or digits 0) (count value))) "0"))
                                                                      value)))
                                                         value)))]
    (when (every? #(some? (value-for %)) template)
      (str/join (map value-for template)))))

(defn workspace-template-number-validation-fn
  "Returns a predicate that checks whether a number exactly matches the workspace's resolved template."
  [workspace]
  (let [{:keys [template prefix separator]} (workspace-number-format workspace)
        pattern                             (template-pattern template prefix separator)]
    (fn [number]
      (boolean (and (string? number)
                    (re-matches pattern number))))))
